import { FC } from 'react';
import { memo } from 'react';
import { Flame } from 'lucide-react';
import { IndustryScore } from '../types';

interface RelevanceScoreDisplayProps {
    relevanceScore?: number; // User's industry-specific score from backend
    industryScores?: IndustryScore[]; // Fallback: all industry scores
}

/**
 * Industry Match Score display:
 * Uses the user's industry-specific relevanceScore from the backend.
 * Falls back to max across all industryScores if relevanceScore is not provided.
 * - Score 8+ → Hot Topic badge
 * - All scores → Show industry match with visual bar
 */
export const RelevanceScoreDisplay: FC<RelevanceScoreDisplayProps> = memo(({ relevanceScore, industryScores }) => {
    // Use user-specific relevanceScore if provided, otherwise fall back to max across all industries
    const score = relevanceScore ?? (industryScores?.length ? Math.max(...industryScores.map(s => s.relevanceScore ?? 0)) : 0);

    if (score < 1) return null;

    const percentage = score * 10;
    const isHot = score >= 8;

    return (
        <div
            className="inline-flex items-center gap-2"
            title={`How closely this topic matches your industry`}
        >
            {/* Hot Topic badge for 8+ */}
            {isHot && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gradient-to-r from-orange-100 to-amber-50 border border-orange-200/50">
                    <Flame className="w-3 h-3 text-orange-500" />
                    <span className="text-[9px] font-bold uppercase tracking-wide text-orange-700">
                        Hot
                    </span>
                </span>
            )}

            {/* Industry Match with visual bar */}
            <div className="inline-flex items-center gap-1.5">
                <span className="text-[10px] text-gray-500 font-medium">
                    Industry Match
                </span>

                {/* Mini progress bar */}
                <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                        className={`h-full rounded-full transition-all ${isHot
                            ? 'bg-gradient-to-r from-orange-400 to-amber-400'
                            : 'bg-gradient-to-r from-indigo-400 to-violet-400'
                            }`}
                        style={{ width: `${percentage}%` }}
                    />
                </div>

                {/* Score */}
                <span className={`text-[11px] font-bold tabular-nums ${isHot ? 'text-orange-600' : 'text-gray-600'}`}>
                    {score}
                </span>
            </div>
        </div>
    );
});

RelevanceScoreDisplay.displayName = 'RelevanceScoreDisplay';
